import { ChangeEvent, JSX, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { AdminMiniTarget } from '../admin-mini.types';
import { LOOKS_LIKE_AN_ADDRESS, isReadyToSend, parsePastedAddresses } from '../admin-mini.rules';

export interface AdminMiniTargetListProps {
    targets: AdminMiniTarget[];
    onChange: (targets: AdminMiniTarget[]) => void;
    /** Which target the editor is currently on, so the list can mark it. */
    editingEmail?: string;
    onEdit: (target: AdminMiniTarget) => void;
    disabled?: boolean;
}

/**
 * Who a run is going to, and the way an admin builds that list.
 *
 * **Typing recipients by hand is the point of the admin tools.** A customer's run picks from the
 * employees on their own account; an admin is testing whoever they like, often people who are in
 * no record anywhere. So this takes a name and an address, and it also takes a **pasted list**,
 * because the realistic way an admin gets six addresses is out of a spreadsheet or an email rather
 * than by typing them one at a time.
 *
 * A row is not ready to send until it has a subject and a body, and the list says which are not
 * rather than leaving the admin to find out when the send refuses them. HQ-API refuses those
 * targets individually and reports them by address, so a run can go out with some rows incomplete,
 * but being told beforehand is better than being told afterwards.
 */
export const AdminMiniTargetList: (props: AdminMiniTargetListProps) => JSX.Element =
    (props: AdminMiniTargetListProps): JSX.Element => {
        const [name, setName] = useState<string>('');
        const [email, setEmail] = useState<string>('');
        const [bulk, setBulk] = useState<string>('');
        const [showBulk, setShowBulk] = useState<boolean>(false);
        const [error, setError] = useState<string>('');

        function alreadyListed(address: string): boolean {
            return props.targets.some(
                (target: AdminMiniTarget): boolean =>
                    target.email.toLowerCase() === address.toLowerCase()
            );
        }

        function addOne(): void {
            const address: string = email.trim();
            if (!LOOKS_LIKE_AN_ADDRESS.test(address)) {
                setError('That does not look like an email address');
                return;
            }
            if (alreadyListed(address)) {
                setError('That address is already on the list');
                return;
            }

            setError('');
            props.onChange([
                ...props.targets,
                { name: name.trim(), email: address, subject: '', emailBody: '' },
            ]);
            setName('');
            setEmail('');
        }

        /**
         * Take whatever was pasted and add the addresses in it. `parsePastedAddresses` decides
         * what counts and drops anything already on the list.
         */
        function addMany(): void {
            const added: AdminMiniTarget[] = parsePastedAddresses(bulk, props.targets);

            if (added.length === 0) {
                setError('No new email addresses in that');
                return;
            }

            setError('');
            props.onChange([...props.targets, ...added]);
            setBulk('');
            setShowBulk(false);
        }

        function remove(address: string): void {
            props.onChange(props.targets.filter(
                (target: AdminMiniTarget): boolean => target.email !== address
            ));
        }

        const incomplete: AdminMiniTarget[] = props.targets.filter(
            (target: AdminMiniTarget): boolean => !isReadyToSend(target)
        );

        return (
            <Stack spacing={ 2 } sx={ { width: '100%' } }>
                <Box>
                    <Typography sx={ {
                        fontSize: 'var(--wf-eyebrow)',
                        letterSpacing: 'var(--wf-track)',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        fontWeight: 700,
                        marginBottom: '10px',
                    } }>
                        { `Recipients (${ props.targets.length })` }
                    </Typography>

                    <Box sx={ { display: 'flex', gap: '8px', alignItems: 'flex-start', flexWrap: 'wrap' } }>
                        <TextField value={ name }
                                   size="small"
                                   label="Name"
                                   placeholder="Optional"
                                   disabled={ props.disabled }
                                   onChange={ (e: ChangeEvent<HTMLInputElement>): void => setName(e.target.value) }
                                   sx={ { flex: '1 1 160px', minWidth: '140px' } } />
                        <TextField value={ email }
                                   size="small"
                                   label="Email"
                                   placeholder="someone@example.com"
                                   error={ error !== '' }
                                   helperText={ error }
                                   disabled={ props.disabled }
                                   onChange={ (e: ChangeEvent<HTMLInputElement>): void => setEmail(e.target.value) }
                                   onKeyDown={ (e): void => {
                                       if (e.key === 'Enter') {
                                           e.preventDefault();
                                           addOne();
                                       }
                                   } }
                                   sx={ { flex: '2 1 240px', minWidth: '200px' } } />
                        <Button variant="contained"
                                size="medium"
                                disabled={ props.disabled }
                                onClick={ addOne }
                                startIcon={ <AddIcon /> }
                                sx={ { marginTop: '1px' } }>
                            { 'Add' }
                        </Button>
                        <Button size="medium"
                                disabled={ props.disabled }
                                onClick={ (): void => setShowBulk(!showBulk) }
                                sx={ { marginTop: '1px' } }>
                            { showBulk ? 'Cancel paste' : 'Paste a list' }
                        </Button>
                    </Box>
                </Box>

                { showBulk && (
                    <Box>
                        <TextField value={ bulk }
                                   multiline
                                   minRows={ 3 }
                                   fullWidth
                                   size="small"
                                   label="Paste addresses"
                                   placeholder="Separated by commas, semicolons, spaces or new lines"
                                   disabled={ props.disabled }
                                   onChange={ (e: ChangeEvent<HTMLInputElement>): void => setBulk(e.target.value) } />
                        <Button variant="contained"
                                size="small"
                                disabled={ props.disabled }
                                onClick={ addMany }
                                sx={ { marginTop: '8px' } }>
                            { 'Add these' }
                        </Button>
                    </Box>
                ) }

                { props.targets.length === 0 && (
                    <Box sx={ {
                        border: '1px dashed',
                        borderColor: 'divider',
                        borderRadius: '10px',
                        padding: '20px',
                        textAlign: 'center',
                        color: 'text.secondary',
                    } }>
                        <Typography sx={ { fontSize: '13px' } }>
                            { 'Nobody on the list yet. Add a recipient above, then write their message.' }
                        </Typography>
                    </Box>
                ) }

                { incomplete.length > 0 && (
                    <Alert severity="warning" icon={ <WarningAmberIcon fontSize="small" /> } sx={ { fontSize: '13px' } }>
                        { incomplete.length === 1
                            ? 'One recipient has no subject or message yet, and will be skipped.'
                            : `${ incomplete.length } recipients have no subject or message yet, and will be skipped.` }
                    </Alert>
                ) }

                <Stack spacing={ 1 }>
                    { props.targets.map((target: AdminMiniTarget): JSX.Element => {
                        const ready: boolean = isReadyToSend(target);
                        const editing: boolean = props.editingEmail === target.email;

                        return (
                            <Box key={ target.email }
                                 sx={ {
                                     display: 'flex',
                                     alignItems: 'center',
                                     justifyContent: 'space-between',
                                     gap: '10px',
                                     padding: '10px 12px',
                                     border: '1px solid',
                                     borderColor: editing ? 'primary.main' : 'divider',
                                     borderRadius: '10px',
                                     background: editing ? 'action.hover' : 'transparent',
                                 } }>
                                <Box sx={ { minWidth: 0 } }>
                                    <Typography sx={ {
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    } }>
                                        { target.name === '' ? target.email : target.name }
                                    </Typography>
                                    { target.name !== '' && (
                                        <Typography sx={ { fontSize: '12px', color: 'text.secondary' } }>
                                            { target.email }
                                        </Typography>
                                    ) }
                                </Box>

                                <Box sx={ { display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 } }>
                                    <Chip size="small"
                                          label={ ready ? 'Ready' : 'No message' }
                                          color={ ready ? 'success' : 'default' }
                                          variant={ ready ? 'filled' : 'outlined' } />
                                    <Tooltip title="Write this recipient's message">
                                        <span>
                                            <IconButton size="small"
                                                        disabled={ props.disabled }
                                                        onClick={ (): void => props.onEdit(target) }
                                                        aria-label={ `Write the message for ${ target.email }` }>
                                                <EditOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Remove from this run">
                                        <span>
                                            <IconButton size="small"
                                                        disabled={ props.disabled }
                                                        onClick={ (): void => remove(target.email) }
                                                        aria-label={ `Remove ${ target.email }` }>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </Box>
                            </Box>
                        );
                    }) }
                </Stack>
            </Stack>
        );
    };
